import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { VOICES } from "@/components/cast/StoryPlayer";

export function NarratorPicker() {
  const [voice, setVoice] = useState(() => localStorage.getItem("cast_voice") || "fable");
  const [playing, setPlaying] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  useEffect(() => () => audio.current?.pause(), []);

  const sample = (v: string) => {
    audio.current?.pause();
    if (playing === v) return setPlaying(null);
    setLoading(v);
    const a = new Audio(`${API}/voice-sample/${v}`);
    a.onplaying = () => { setLoading(null); setPlaying(v); };
    a.onended = a.onpause = () => setPlaying((p) => (p === v ? null : p));
    a.onerror = () => { setLoading(null); setPlaying(null); toast.error("Voice preview is unavailable right now"); };
    audio.current = a;
    a.play().catch(() => setLoading(null));
  };
  const choose = (v: string) => { setVoice(v); localStorage.setItem("cast_voice", v); toast.success("Narrator voice saved"); };

  return (
    <section className="card-warm p-6 sm:p-8" data-testid="narrator-picker">
      <p className="eyebrow">Narrator voice</p>
      <p className="mt-1 text-sm text-slate-500">Tap play to hear each narrator say hello, then pick the one you like.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {VOICES.map((v) => {
          const active = voice === v.id;
          return (
            <div key={v.id} data-testid={`narrator-option-${v.id}`}
              className={`flex items-center gap-3 rounded-2xl border p-3 transition-colors ${active ? "border-[#C86D51] bg-[#FBF3EC]" : "border-[#E2DBCE] bg-white"}`}>
              <button data-testid={`narrator-sample-${v.id}`} onClick={() => sample(v.id)} aria-label={`Hear ${v.label}`}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1E293B] text-white transition-transform hover:scale-105">
                {loading === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : playing === v.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
              <span className="min-w-0 flex-1"><span className="block font-semibold text-slate-900">{v.label}</span><span className="block truncate text-xs text-slate-500">{v.hint}</span></span>
              <button data-testid={`narrator-choose-${v.id}`} onClick={() => choose(v.id)} disabled={active}
                className={`flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${active ? "text-[#C86D51]" : "bg-[#F3ECE0] text-slate-800 hover:bg-[#E9DFCF]"}`}>
                {active ? <><Check className="h-3.5 w-3.5" />Chosen</> : "Choose"}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
